import React, { useState } from 'react';
import Icons from '../components/Icons';
import FooterSection from '../components/Footer';

const ContactPage = ({ isDark }) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });

  // Submission status state
  const [status, setStatus] = useState({
    loading: false,
    success: false,
    error: null
  });

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form fields
    if (!formData.name.trim() || !formData.phone.trim() || !formData.message.trim()) {
      setStatus({
        loading: false,
        success: false,
        error: 'All fields are required. Please fill in all the details.'
      });
      return;
    }

    // Set loading state
    setStatus({
      loading: true,
      success: false,
      error: null
    });

    try {
      const response = await fetch('/api/contact.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          phone: formData.phone.trim(),
          message: formData.message.trim()
        })
      });

      const data = await response.json();

      if (data.success) {
        // Success - reset form and show success message
        setFormData({
          name: '',
          phone: '',
          message: ''
        });
        setStatus({
          loading: false,
          success: true,
          error: null
        });
      } else {
        // API returned failure
        setStatus({
          loading: false,
          success: false,
          error: data.message || 'Failed to submit form. Please try again.'
        });
      }
    } catch (err) {
      // Network or other error
      setStatus({
        loading: false,
        success: false,
        error: 'Unable to connect to the server. Please check your connection and try again.'
      });
    }
  };

  return (
    <>
      <section className={`relative py-32 transition-colors duration-300 ${isDark ? 'bg-[#0a0a0a]' : 'bg-gray-900'}`}>
        <div className={`absolute inset-0 ${isDark ? 'bg-gradient-to-b from-[#1a1a1a] to-[#0a0a0a]' : 'bg-gradient-to-b from-gray-800 to-gray-900'}`}></div>
        <div className="relative z-10 container-custom text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-4 text-white">Contact <span className="text-[#c9a962]">Us</span></h1>
          <p className="text-xl text-gray-400">Get in touch with us for your premium car rental needs</p>
        </div>
      </section>
      <section className={`py-20 transition-colors duration-300 ${isDark ? 'bg-[#0d0d0d]' : 'bg-gray-100'}`}>
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div>
              <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>Get in <span className="text-[#c9a962]">Touch</span></h2>
              <div className="space-y-6">
                <div className={`flex items-start gap-4 p-6 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                  <div className="w-12 h-12 bg-[#c9a962]/20 rounded-full flex items-center justify-center text-[#c9a962]">
                    <Icons.MapMarker />
                  </div>
                  <div>
                    <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Location</h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>Udaipur, Rajasthan, India</p>
                  </div>
                </div>
                {/* Google Maps Embed */}
                <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-[#1a1a1a]' : 'bg-white'}`}>
                  <iframe 
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3627.6556892554504!2d73.72805897494952!3d24.601076155588437!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3967e5005edc66f1%3A0x1ce34447c597f4b4!2sBrothers%20Rental!5e0!3m2!1sen!2sin!4v1770891919849!5m2!1sen!2sin" 
                    width="100%" 
                    height="250" 
                    style={{ border: 0 }} 
                    allowFullScreen="" 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Brothers Rental Location"
                  ></iframe>
                </div>
                <div className={`flex items-start gap-4 p-6 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                  <div className="w-12 h-12 bg-[#25D366]/20 rounded-full flex items-center justify-center text-[#25D366]">
                    <Icons.WhatsApp />
                  </div>
                  <div>
                    <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>WhatsApp</h3>
                    <a href="https://wa.me/919602360369" target="_blank" rel="noopener noreferrer" className="text-[#25D366] hover:underline">96023 60369</a>
                  </div>
                </div>
                <div className={`flex items-start gap-4 p-6 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                  <div className="w-12 h-12 bg-[#c9a962]/20 rounded-full flex items-center justify-center text-[#c9a962]">
                    <Icons.Mail />
                  </div>
                  <div>
                    <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Email</h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>brotherscarrental8286@gmail.com</p>
                  </div>
                </div>
                <div className={`flex items-start gap-4 p-6 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                  <div className="w-12 h-12 bg-[#c9a962]/20 rounded-full flex items-center justify-center text-[#c9a962]">
                    <Icons.Phone />
                  </div>
                  <div>
                    <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Phone</h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>96023 60369</p>
                  </div>
                </div>
                <div className={`flex items-start gap-4 p-6 rounded-xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                  <div className="w-12 h-12 bg-[#c9a962]/20 rounded-full flex items-center justify-center text-[#c9a962]">
                    <Icons.Clock />
                  </div>
                  <div>
                    <h3 className={`font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>Working Hours</h3>
                    <p className={isDark ? 'text-gray-400' : 'text-gray-600'}>24/7 Available</p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <h2 className={`text-3xl font-bold mb-8 ${isDark ? 'text-white' : 'text-gray-900'}`}>Send us a <span className="text-[#c9a962]">Message</span></h2>
              
              {/* Success Message */}
              {status.success && (
                <div className="mb-6 p-4 rounded-lg bg-green-500/20 border border-green-500 text-green-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="font-medium">Thank you! Your message has been sent successfully. We'll get back to you soon.</span>
                  </div>
                </div>
              )}

              {/* Error Message */}
              {status.error && (
                <div className="mb-6 p-4 rounded-lg bg-red-500/20 border border-red-500 text-red-500">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="font-medium">{status.error}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} className={`space-y-6 p-8 rounded-2xl ${isDark ? 'bg-[#1a1a1a]' : 'bg-white shadow-lg'}`}>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Your Name</label>
                  <input 
                    type="text" 
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Enter your name" 
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-gray-50 text-gray-900 border border-gray-200 focus:border-[#c9a962]'}`} 
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Phone Number</label>
                  <input 
                    type="tel" 
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="Enter your phone" 
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-gray-50 text-gray-900 border border-gray-200 focus:border-[#c9a962]'}`} 
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Message</label>
                  <textarea 
                    rows="4" 
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we help you?" 
                    className={`w-full px-4 py-3 rounded-lg transition-all duration-300 ${isDark ? 'bg-[#0d0d0d] text-white border border-gray-700 focus:border-[#c9a962]' : 'bg-gray-50 text-gray-900 border border-gray-200 focus:border-[#c9a962]'}`}
                  ></textarea>
                </div>
                <button 
                  type="submit" 
                  disabled={status.loading}
                  className={`w-full py-4 bg-gradient-to-r from-[#c9a962] to-[#a08040] text-black font-semibold uppercase tracking-wider hover:shadow-lg hover:shadow-[#c9a962]/30 transition-all duration-300 rounded-lg flex items-center justify-center gap-2 ${status.loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                >
                  {status.loading ? (
                    <>
                      <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>Sending...</span>
                    </>
                  ) : (
                    'Send Message'
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
      <FooterSection isDark={isDark} />
    </>
  );
};

export default ContactPage;
